import { motion } from "framer-motion";
import { Car, Truck, FileText, Info, Calculator } from "lucide-react";
import { useState } from "react";

const vehicleTypes = [
  {
    icon: Car,
    code: "8703",
    title: "Легковой автомобиль",
    color: "blue",
    description: "Моторные транспортные средства, предназначенные главным образом для перевозки людей",
    details: [
      "До 9 мест для сидения, включая водителя",
      "Категория M1/M1G по техрегламенту",
      "Включает грузопассажирские автомобили-фургоны",
      "Включает гоночные автомобили",
    ],
    examples: "Седаны, хэтчбеки, универсалы, кроссоверы, внедорожники, минивэны",
  },
  {
    icon: Truck,
    code: "8704",
    title: "Грузовой автомобиль",
    color: "orange",
    description: "Моторные транспортные средства, предназначенные для перевозки грузов",
    details: [
      "Классификация по полной массе (до 5 т, 5-20 т, более 20 т)",
      "Категория N1/N2/N3 по техрегламенту",
      "Включает самосвалы, фургоны, рефрижераторы",
      "Основное назначение — перевозка грузов",
    ],
    examples: "Грузовики, фургоны, бортовые, тягачи, самосвалы",
  },
];

const pickupCriteria = [
  {
    condition: "Более двух осей",
    result: "грузовой",
  },
  {
    condition: "Полная масса более 3500 кг",
    result: "грузовой",
  },
  {
    condition: "Длина грузовой платформы > 50% колёсной базы",
    result: "грузовой",
  },
  {
    condition: "P − (M + N × 68) > N × 68",
    result: "грузовой",
  },
];

export default function VehicleClassificationSection() {
  const [showPickupCalc, setShowPickupCalc] = useState(false);
  const [pickupData, setPickupData] = useState({
    P: "", // Полная масса
    M: "", // Снаряжённая масса
    N: "", // Число мест кроме водителя
  });
  const [pickupResult, setPickupResult] = useState(null);

  const calculatePickup = () => {
    const P = parseFloat(pickupData.P);
    const M = parseFloat(pickupData.M);
    const N = parseInt(pickupData.N);
    
    if (isNaN(P) || isNaN(M) || isNaN(N)) return;
    
    // Формула: P - (M + N × 68) > N × 68
    const leftSide = P - (M + N * 68);
    const rightSide = N * 68;
    const isGruzovoy = leftSide > rightSide;
    
    setPickupResult({
      isGruzovoy,
      leftSide: Math.round(leftSide),
      rightSide: Math.round(rightSide),
      code: isGruzovoy ? "8704" : "8703",
      type: isGruzovoy ? "Грузовой автомобиль" : "Легковой автомобиль",
    });
  };

  const resetCalculator = () => {
    setPickupData({ P: "", M: "", N: "" });
    setPickupResult(null);
  };

  return (
    <section 
      id="classification" 
      className="py-20 md:py-24 bg-[#0A0A0A]"
      data-testid="classification-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-[#0F0F0F] text-white/50 px-4 py-2 rounded-full text-sm font-medium mb-4 border border-white/10">
            <FileText className="w-4 h-4" />
            ТН ВЭД ЕАЭС
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4">
            Классификация автомобилей
          </h2>
          <p className="text-base md:text-lg text-white/50 max-w-2xl mx-auto">
            Определения согласно Товарной номенклатуре внешнеэкономической деятельности ЕАЭС
          </p>
        </motion.div>

        {/* Vehicle Types */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {vehicleTypes.map((type, index) => (
            <motion.div
              key={type.code}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className="bg-[#0F0F0F] rounded-2xl p-6 md:p-8 border border-white/10 hover:shadow-lg transition-shadow"
              data-testid={`vehicle-type-${type.code}`}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                  type.color === "blue" ? "bg-blue-100" : "bg-orange-100"
                }`}>
                  <type.icon className={`w-7 h-7 ${
                    type.color === "blue" ? "text-blue-600" : "text-orange-600"
                  }`} />
                </div>
                <div>
                  <div className={`inline-block px-3 py-1 rounded-full text-sm font-bold mb-1 ${
                    type.color === "blue" ? "bg-blue-500/20 text-blue-400" : "bg-orange-500/20 text-orange-400"
                  }`}>
                    Код {type.code}
                  </div>
                  <h3 className="text-xl font-bold text-white">{type.title}</h3>
                </div>
              </div>
              
              <p className="text-white/50 mb-4 font-medium">
                {type.description}
              </p>
              
              <ul className="space-y-2 mb-4">
                {type.details.map((detail, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-white/50">
                    <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${
                      type.color === "blue" ? "bg-blue-500" : "bg-orange-500"
                    }`} />
                    {detail}
                  </li>
                ))}
              </ul>
              
              <div className="pt-4 border-t border-white/10">
                <p className="text-xs text-white/40">
                  <span className="font-medium">Примеры:</span> {type.examples}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Pickup Classification */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-none p-6 md:p-10 text-white"
          data-testid="pickup-classification"
        >
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Info */}
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 bg-[#0F0F0F]/10 px-3 py-1 rounded-full text-sm font-medium mb-4">
                <Info className="w-4 h-4" />
                Решение КТС № 751 от 16.08.2011
              </div>
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                Классификация пикапов
              </h3>
              <p className="text-white/40 mb-6">
                Автомобили типа "Pickup" с грузовой платформой классифицируются как 
                <span className="text-orange-400 font-semibold"> грузовые (8704)</span>, если выполняется 
                <span className="text-white font-medium"> любое</span> из условий:
              </p>
              
              <div className="space-y-3 mb-6">
                {pickupCriteria.map((criterion, index) => (
                  <div key={index} className="flex items-center gap-3 bg-[#0F0F0F]/5 rounded-sm p-3">
                    <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center text-orange-400 font-bold text-sm">
                      {index + 1}
                    </div>
                    <span className="text-[#E2E8F0]">{criterion.condition}</span>
                  </div>
                ))}
              </div>
              
              <p className="text-sm text-white/50">
                В остальных случаях пикап классифицируется как <span className="text-blue-400 font-semibold">легковой (8703)</span>
              </p>
            </div>

            {/* Calculator */}
            <div className="lg:w-96">
              <div className="bg-[#0F0F0F]/10 backdrop-blur rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Calculator className="w-5 h-5 text-[#F97316]" />
                  <h4 className="font-semibold">Калькулятор пикапа</h4>
                </div>
                
                {!showPickupCalc ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-white/40 mb-4">
                      Проверьте классификацию вашего пикапа по формуле
                    </p>
                    <button
                      onClick={() => setShowPickupCalc(true)}
                      className="bg-[#F97316] hover:bg-[#EA580C] text-white px-6 py-3 rounded-sm font-semibold transition-colors"
                      data-testid="start-pickup-calc"
                    >
                      Рассчитать
                    </button>
                  </div>
                ) : pickupResult ? (
                  <div className="text-center">
                    <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-3 ${
                      pickupResult.isGruzovoy ? "bg-orange-500/20" : "bg-blue-500/20"
                    }`}>
                      {pickupResult.isGruzovoy ? (
                        <Truck className="w-8 h-8 text-orange-400" />
                      ) : (
                        <Car className="w-8 h-8 text-blue-400" />
                      )}
                    </div>
                    <p className={`text-lg font-bold mb-1 ${
                      pickupResult.isGruzovoy ? "text-orange-400" : "text-blue-400"
                    }`}>
                      Код {pickupResult.code}
                    </p>
                    <p className="text-white font-medium mb-2">{pickupResult.type}</p>
                    <p className="text-xs text-white/40 mb-4">
                      {pickupResult.leftSide} {pickupResult.isGruzovoy ? ">" : "≤"} {pickupResult.rightSide}
                    </p>
                    <button
                      onClick={resetCalculator}
                      className="text-sm text-white/40 hover:text-white"
                    >
                      Рассчитать заново
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs text-white/40 mb-1 block">
                        P — полная масса (кг)
                      </label>
                      <input
                        type="number"
                        value={pickupData.P}
                        onChange={(e) => setPickupData({ ...pickupData, P: e.target.value })}
                        placeholder="например: 3500"
                        className="w-full bg-[#0F0F0F]/10 border border-white/20 rounded-sm px-4 py-3 text-white placeholder-[#64748B] focus:outline-none focus:border-[#F97316]"
                        data-testid="pickup-input-p"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-white/40 mb-1 block">
                        M — снаряжённая масса (кг)
                      </label>
                      <input
                        type="number"
                        value={pickupData.M}
                        onChange={(e) => setPickupData({ ...pickupData, M: e.target.value })}
                        placeholder="например: 1750"
                        className="w-full bg-[#0F0F0F]/10 border border-white/20 rounded-sm px-4 py-3 text-white placeholder-[#64748B] focus:outline-none focus:border-[#F97316]"
                        data-testid="pickup-input-m"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-white/40 mb-1 block">
                        N — число мест (кроме водителя)
                      </label>
                      <input
                        type="number"
                        value={pickupData.N}
                        onChange={(e) => setPickupData({ ...pickupData, N: e.target.value })}
                        placeholder="например: 4"
                        className="w-full bg-[#0F0F0F]/10 border border-white/20 rounded-sm px-4 py-3 text-white placeholder-[#64748B] focus:outline-none focus:border-[#F97316]"
                        data-testid="pickup-input-n"
                      />
                    </div>
                    <div className="pt-2 flex gap-2">
                      <button
                        onClick={calculatePickup}
                        disabled={!pickupData.P || !pickupData.M || !pickupData.N}
                        className="flex-1 bg-[#F97316] hover:bg-[#EA580C] disabled:bg-[#F97316]/50 text-white py-3 rounded-sm font-semibold transition-colors"
                        data-testid="pickup-calc-btn"
                      >
                        Рассчитать
                      </button>
                      <button
                        onClick={() => setShowPickupCalc(false)}
                        className="px-4 py-3 rounded-sm bg-[#0F0F0F]/10 hover:bg-[#0F0F0F]/20 transition-colors"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}
                
                <div className="mt-4 pt-4 border-t border-white/10">
                  <p className="text-xs text-white/50">
                    <span className="text-[#F97316]">Формула:</span> P − (M + N × 68) {">"} N × 68
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Official Link */}
          <div className="mt-8 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-white/50">
              Официальный документ: Решение Комиссии Таможенного союза № 751 от 16.08.2011
            </p>
            <a
              href="https://www.alta.ru/tamdoc/11sr0751/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#0F0F0F]/10 hover:bg-[#0F0F0F]/20 px-4 py-2 rounded-full text-sm font-medium transition-colors"
              data-testid="pickup-decision-link"
            >
              <FileText className="w-4 h-4" />
              Текст решения
            </a>
          </div>
        </motion.div>

        {/* Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mt-8 bg-amber-50 border border-amber-200 rounded-2xl p-6"
        >
          <div className="flex gap-4">
            <Info className="w-6 h-6 text-amber-600 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-amber-800 mb-1">Важно!</h4>
              <p className="text-amber-700 text-sm">
                Классификация влияет на размер таможенных пошлин. Легковые автомобили (8703) растамаживаются 
                по ставкам, зависящим от возраста и объёма двигателя. Грузовые (8704) — по другим тарифам. 
                Для точной классификации рекомендуем обратиться к нашим специалистам.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
